import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { readConfig } from "../lib/config";
import { usePortalAuth } from "../hooks/use-portal-auth";
import { ConfigError } from "@pgs-core/ui/components/config-error";
import { LoginScreen } from "@pgs-core/ui/components/login-screen";
import { SetPasswordScreen } from "@pgs-core/ui/components/set-password-screen";
import { ForbiddenScreen } from "@pgs-core/ui/components/forbidden-screen";
import { LoadingScreen } from "@pgs-core/ui/components/loading-screen";
import { PortalShell } from "./portal-shell";
import { ActAsExpiryModal } from "./act-as-expiry-modal";
import { DashboardPage } from "../pages/dashboard-page";
import { PagesPage } from "../pages/pages-page";
import { PageEditorPage } from "../pages/page-editor-page";
import { MenusPage } from "../pages/menus-page";
import { GalleriesPage } from "../pages/galleries-page";
import { GalleryDetailPage } from "../pages/gallery-detail-page";
import { MediaLibraryPage } from "../pages/media-library-page";
import { SettingsPage } from "../pages/settings-page";
import { AccountPage } from "../pages/account-page";
import { FormsPage } from "../pages/forms-page";
import { FormsBuildPage } from "../pages/forms-build-page";
import { FormBuilderPage } from "../pages/form-builder-page";
import { ModulesPage } from "../pages/modules-page";
import { BillingPage } from "../pages/billing-page";
import { BillingPlanPage } from "../pages/billing-plan-page";
import { BillingInvoicesPage } from "../pages/billing-invoices-page";
import { PostsPage } from "../pages/posts-page";
import { PostEditorPage } from "../pages/post-editor-page";
import { EventsPage } from "../pages/events-page";
import { EventEditorPage } from "../pages/event-editor-page";
import { BookingCalendarPage } from "../pages/booking-calendar-page";
import { BookingServicesPage } from "../pages/booking-services-page";
import { BookingSettingsPage } from "../pages/booking-settings-page";
import { BookingBlockedPage } from "../pages/booking-blocked-page";
import { SettingsHoursPage } from "../pages/settings-hours-page";
import { MenuMenusPage } from "../pages/menu-menus-page";
import { MenuItemsPage } from "../pages/menu-items-page";
import { MenuQrPage } from "../pages/menu-qr-page";
import { MenuEditorPage } from "../pages/menu-editor-page";
import { SupportPage } from "../pages/support-page";

const config = readConfig();

// Old bookmarks: /galleries/:id lives under Media now.
function GalleryRedirect() {
  const { id } = useParams();
  return <Navigate to={`/media/galleries/${id}`} replace />;
}

export function App() {
  const auth = usePortalAuth(config);
  const { view, error, api, user, impersonation } = auth;

  if (view === "config-error") return <ConfigError message={error} />;
  if (view === "loading") return <LoadingScreen message="Checking your session" />;
  if (view === "login") return <LoginScreen title="Client Portal" onSignIn={auth.signIn} error={error} />;
  if (view === "set-password") {
    return <SetPasswordScreen email={auth.recoveryEmail} onSetPassword={auth.completeSetPassword} error={error} />;
  }
  if (view === "forbidden") {
    return <ForbiddenScreen title="Client account required" message={error} onRetry={auth.signOut} onSignOut={auth.signOut} />;
  }

  return (
    <BrowserRouter>
      {/* Act-as tokens are long-lived but not refreshable: when one expires,
          this takes over the screen and points back to the Admin Portal. */}
      {impersonation && api ? (
        <ActAsExpiryModal
          api={api}
          returnTo={impersonation.returnTo}
          expiredSignal={auth.authExpired}
        />
      ) : null}
      <Routes>
        <Route
          element={
            <PortalShell
              user={user}
              api={api}
              tenantId={auth.tenantId}
              environment={auth.environment}
              impersonation={impersonation}
              onSignOut={auth.signOut}
              changePassword={auth.changePassword}
            />
          }
        >
          <Route index element={<DashboardPage />} />
          <Route path="pages" element={<PagesPage />} />
          <Route path="pages/:slug" element={<PageEditorPage />} />
          <Route path="menus" element={<MenusPage />} />
          {/* Media: Library / Galleries tabs, plus the per-gallery editor. */}
          <Route path="media" element={<MediaLibraryPage />} />
          <Route path="media/galleries" element={<GalleriesPage />} />
          <Route path="media/galleries/:id" element={<GalleryDetailPage />} />
          {/* Legacy gallery URLs redirect into the Media section. */}
          <Route path="galleries" element={<Navigate to="/media/galleries" replace />} />
          <Route path="galleries/:id" element={<GalleryRedirect />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="settings/hours" element={<SettingsHoursPage />} />
          <Route path="account" element={<AccountPage />} />
          {/* Forms module: one page with Inbox / Forms tabs, plus the per-form
              builder drill-in. The static "build" segment outranks ":id", so
              that tab route is safe. */}
          <Route path="forms" element={<FormsPage />} />
          <Route path="forms/build" element={<FormsBuildPage />} />
          <Route path="forms/:id" element={<FormBuilderPage />} />
          <Route path="modules" element={<ModulesPage />} />
          <Route path="billing" element={<BillingPage />} />
          <Route path="billing/plan" element={<BillingPlanPage />} />
          <Route path="billing/invoices" element={<BillingInvoicesPage />} />
          {/* Posts (blog) module: list + per-post editor. */}
          <Route path="blog" element={<PostsPage />} />
          <Route path="blog/:id" element={<PostEditorPage />} />
          {/* Events module: list + per-event editor. */}
          <Route path="events" element={<EventsPage />} />
          <Route path="events/:id" element={<EventEditorPage />} />

          {/* Priority-support SLA module: request an update + history. */}
          <Route path="support" element={<SupportPage />} />

          <Route path="bookings" element={<BookingCalendarPage />} />
          <Route path="bookings/services" element={<BookingServicesPage />} />
          <Route path="bookings/blocked" element={<BookingBlockedPage />} />
          <Route path="bookings/settings" element={<BookingSettingsPage />} />
          {/* Old bookmark: availability became per-service hours on Services. */}
          <Route path="bookings/availability" element={<Navigate to="/bookings/services" replace />} />
          {/* Menu module: one page with Menus / Items / QR codes tabs, plus the
              per-menu editor drill-in. Static segments ("items", "qr") outrank
              ":menuId", so those tab routes are safe. */}
          <Route path="menu" element={<MenuMenusPage />} />
          <Route path="menu/items" element={<MenuItemsPage />} />
          <Route path="menu/qr" element={<MenuQrPage />} />
          <Route path="menu/:menuId" element={<MenuEditorPage />} />
          {/* Catch-all: redirect unknown paths to the dashboard */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
